import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { dashboardLoginUrl } from './config';
import { isAuthenticated } from './session';
import { driftedStore, pinStore, subscribeStoreDrift } from './storePin';
import { installStoreWatch } from './storeWatch';
import { refreshSession } from '../lib/http';
import { useBuilderStore } from '../state/builderStore';
import { bt, btp } from '../i18n';

type BootState = 'refreshing' | 'authenticated' | 'redirecting';

/**
 * BuilderBoot — the silent-refresh auth gate (build-plan §3.3). The builder has NO login UI of its own
 * (login lives in the dashboard), so on mount it carries no token (a full cross-origin nav from the
 * dashboard brings no JS memory). It immediately exchanges the HttpOnly `aqx_refresh` cookie for a
 * fresh access token via `POST /api/auth/seller/refresh`:
 *
 *  - while refreshing → a loading state (not a spinner-only screen; skeleton chrome).
 *  - 200 → stash the token in memory only (never localStorage) and render the editor.
 *  - failure (no/expired cookie, 401, network) → bounce to the dashboard login carrying `returnUrl`.
 *
 * The same-site `SameSite=Strict` cookie reaches the API because `builder.* ↔ api.*` share the
 * registrable site (and `localhost:3000 ↔ localhost:8080` is one site in dev) — build-plan §3.4.
 */
export function BuilderBoot({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<BootState>('refreshing');
  // StrictMode double-invokes effects in dev; guard so we issue exactly one refresh (rotation-safe).
  const started = useRef(false);

  useEffect(() => {
    // StrictMode double-invokes effects in dev. The ref guard issues exactly ONE refresh (rotation-safe),
    // so we deliberately do NOT cancel on cleanup: with a single-issue guard, the StrictMode unmount/remount
    // would otherwise orphan the only in-flight refresh (its handler cancelled by the first effect's
    // cleanup), stalling the boot on "Loading…". refreshSession() is itself single-flight (lib/http).
    if (started.current) return;
    started.current = true;
    // A superadmin consultation arrives with its token already seeded (view-handoff, main.tsx):
    // no cookie exchange — the consultation must never pick up whatever seller cookie this
    // browser happens to hold.
    if (isAuthenticated()) {
      pinStore();
      setState('authenticated');
      void useBuilderStore.getState().loadStore();
      return;
    }
    void refreshSession().then((ok) => {
      if (ok) {
        // Pin the store BEFORE anything else runs: from here on, every refresh is measured against the
        // shop this document actually authenticated on (see boot/storePin).
        pinStore();
        setState('authenticated');
        // Fire-and-forget: load the store slug so the topbar "Preview" button can open the public
        // storefront. Best-effort + idempotent (builderStore.loadStore) — never blocks the editor.
        void useBuilderStore.getState().loadStore();
      } else {
        setState('redirecting');
        window.location.assign(dashboardLoginUrl());
      }
    });
  }, []);

  if (state === 'authenticated') return <StoreGate>{children}</StoreGate>;

  return (
    <div className="bld-boot" role="status" aria-live="polite">
      <div className="bld-boot__panel">
        <div className="bld-boot__spinner" aria-hidden="true" />
        <p className="bld-boot__text">
          {state === 'redirecting'
            ? bt('chrome.boot.redirect', 'Redirecting to sign in…')
            : bt('chrome.boot.loading', 'Loading your builder…')}
        </p>
      </div>
    </div>
  );
}

/**
 * StoreGate — the editor, unless the ACCOUNT has moved to another store since this tab was opened.
 *
 * Wraps the whole builder rather than living inside the shell, because it must outrank everything the
 * shell can show: the canvas, the generation takeover, the conflict UI. All of them would be talking
 * about a shop the seller has left.
 *
 * There is exactly one way out and it is a reload. Not a nicety — the store lives in a signed token
 * minted at boot, so switching this document onto the new shop means re-minting, and re-minting means
 * booting. Offering "continue anyway" would offer to keep editing the wrong store.
 *
 * Unsaved work is not at risk here: the tab lost focus for the switch to happen at all, and
 * `visibilitychange` → hidden already flushed the draft (build-plan §5.4) — into the store it belongs
 * to, which is the one this document is still holding.
 */
function StoreGate({ children }: { children: React.ReactNode }) {
  // Installed once the boot gate has opened, so the watch never races the refresh that mints the pin.
  useEffect(() => installStoreWatch(), []);
  const drifted = useSyncExternalStore(subscribeStoreDrift, driftedStore, () => null);
  if (!drifted) return <>{children}</>;
  return (
    <div className="bld-boot" role="alert">
      <div className="bld-boot__panel">
        <h1 className="bld-boot__title">
          {bt('chrome.store_changed.title', 'You switched to another store')}
        </h1>
        <p className="bld-boot__text">
          {drifted.name
            ? btp(
                'chrome.store_changed.body_named',
                'This editor is still showing the store it was opened on. Reload it to work on {store}.',
                { store: drifted.name },
              )
            : bt(
                'chrome.store_changed.body',
                'This editor is still showing the store it was opened on. Reload it to work on the store you are in now.',
              )}
        </p>
        <button
          type="button"
          className="bld-btn bld-btn--primary"
          onClick={() => window.location.reload()}
        >
          {bt('chrome.store_changed.action', 'Reload the editor')}
        </button>
      </div>
    </div>
  );
}
